<script setup lang="ts">
import {computed} from "vue";
import {Activity, Cake, Eye, Link, Route, Settings, Sparkles, UserRoundCheck, Zap} from "@lucide/vue";
import {storeToRefs} from "pinia";
import {Switch} from "@/core/components/ui/switch";
import {AgendaViewKey} from "@/agenda/lib/agendaViews";
import {useAiAssistantStore} from "@/stores/aiAssistant.store";
import {PLANNING_ASSISTANT} from "@/ai-assistant/constants";

/**
 * What the agenda shows on its grid and where it leads, as two cards: the
 * agenda screen stacks them in its right rail, above the day tasks, and the
 * toolbar opens them in a popover wherever that rail is not drawn.
 *
 * A patient file is one patient's agenda: neither the pharmacy's birthdays
 * nor the screens that plan for the whole team belong there.
 */
const props = defineProps<{
    view: AgendaViewKey;
    compact?: boolean;
    /** The pharmacy has load data and the view can overlay it. */
    frequentationAvailable: boolean;
    birthdayCount: number;
    bookingEnabled: boolean;
    /**
     * In the toolbar popover: the cards float over the page and need their own
     * shadow, and the toolbar holds the assistant toggle.
     */
    floating?: boolean;
    /** Whether the agenda offers the assistant at all. */
    assistantOffered?: boolean;
}>();

const presence = defineModel<boolean>('presence', {required: true});
const frequentation = defineModel<boolean>('frequentation', {required: true});
const birthdayOverlay = defineModel<boolean>('birthdayOverlay', {required: true});

const emit = defineEmits<{
    'open-birthdays': [];
    'open-booking-link': [];
}>();

const assistant = useAiAssistantStore();
const {isOpen: assistantOpen, minimized: assistantMinimized} = storeToRefs(assistant);

/**
 * The assistant row is the rail's alone: wherever the cards sit in the
 * toolbar popover, the toolbar carries the assistant's own toggle beside it,
 * and one way in per screen is enough.
 */
const assistantRow = computed(() => props.assistantOffered && !props.compact && !props.floating);

/** The row toggles the window, so it reads as on while the window shows. */
const assistantShown = computed(() => assistantOpen.value && !assistantMinimized.value);

/** Only the day view draws presence lanes. */
const presenceOffered = computed(() => props.view === 'day');

/**
 * The day view lists the day's birthdays in a panel; the other views paint
 * them on the grid, an overlay switched on demand.
 */
const birthdaysOffered = computed(() => !props.compact);

const displayEmpty = computed(() =>
    !presenceOffered.value && !props.frequentationAvailable && !birthdaysOffered.value);

const cardClass = computed(() => [
    'rounded-lg border border-gray-200 bg-white p-3',
    props.floating ? 'shadow-lg' : ''
]);

const rowClass = 'flex h-8 w-full cursor-pointer items-center gap-2.5 rounded-md px-1.5 text-left text-[13px] text-gray-800 transition-colors hover:bg-gray-50';
</script>

<template>
  <div class="flex flex-col gap-3">
    <section v-if="!displayEmpty" :class="cardClass">
      <header class="mb-1.5 flex items-center gap-1.5">
        <Eye class="size-4 text-primary" />
        <h2 class="text-xs font-semibold text-gray-800">
          Afficher sur l'agenda
        </h2>
      </header>
      <!-- The row reads in the brand violet while its switch is on, so the
           state shows in the wording too, not in the switch alone. -->
      <label v-if="presenceOffered" :class="[rowClass, presence ? 'font-medium text-primary' : '']">
        <UserRoundCheck class="size-4 shrink-0" :class="presence ? 'text-primary' : 'text-gray-500'" />
        Présence de l'équipe
        <Switch v-model="presence" class="ml-auto" />
      </label>
      <label v-if="frequentationAvailable" :class="[rowClass, frequentation ? 'font-medium text-primary' : '']">
        <Activity class="size-4 shrink-0" :class="frequentation ? 'text-primary' : 'text-gray-500'" />
        Fréquentation prévue
        <Switch v-model="frequentation" class="ml-auto" />
      </label>
      <template v-if="birthdaysOffered">
        <button v-if="view === 'day'" type="button" :class="rowClass" @click="emit('open-birthdays')">
          <Cake class="size-4 shrink-0 text-primary" />
          Anniversaires du jour
          <span
            v-if="birthdayCount"
            class="ml-auto rounded-full bg-pink/20 px-1.5 text-xs font-semibold text-gray-dark"
          >
            {{ birthdayCount }}
          </span>
        </button>
        <label v-else :class="[rowClass, birthdayOverlay ? 'font-medium text-primary' : '']">
          <Cake class="size-4 shrink-0" :class="birthdayOverlay ? 'text-primary' : 'text-pink'" />
          Anniversaires
          <Switch v-model="birthdayOverlay" class="ml-auto" />
        </label>
      </template>
    </section>
    <section :class="cardClass">
      <header class="mb-1.5 flex items-center gap-1.5">
        <Zap class="size-4 text-primary" />
        <h2 class="text-xs font-semibold text-gray-800">
          Raccourcis
        </h2>
      </header>
      <button v-if="bookingEnabled" type="button" :class="rowClass" @click="emit('open-booking-link')">
        <Link class="size-4 shrink-0 text-gray-500" />
        Lien de prise de RDV
      </button>
      <!-- A route rather than sparkles: those belong to the assistant, and
           the two rows side by side read as the same thing twice. -->
      <router-link v-if="!compact" :to="{name: 'agenda.health-journey'}" :class="rowClass">
        <Route class="size-4 shrink-0 text-gray-500" />
        Parcours de santé
      </router-link>
      <!-- No type attribute: Vuetify's reset on [type=button] comes after
           Tailwind and would put back the inherited colour. -->
      <button
        v-if="assistantRow"
        :class="[rowClass, assistantShown ? 'font-medium text-primary' : '']"
        :aria-pressed="assistantShown"
        @click="assistant.toggle(PLANNING_ASSISTANT)"
      >
        <span class="flex size-4 shrink-0 items-center justify-center rounded bg-gradient-to-r from-primary-dark to-primary">
          <Sparkles class="size-3 text-white" />
        </span>
        {{ assistantShown ? "Masquer l'assistant" : "Demander à l'IA" }}
      </button>
      <router-link :to="{name: 'agenda.appointment-types'}" :class="rowClass">
        <Settings class="size-4 shrink-0 text-gray-500" />
        Configuration des RDV
      </router-link>
    </section>
  </div>
</template>
